/**
 * Config storage service for the test application (Story 302).
 *
 * Splits a `ClaudeConfig` into two storage layers per the security challenge
 * notes on issue #14:
 *
 * - Non-secret fields (model, maxTokens, temperature, baseUrl,
 *   anthropicVersion) live in `localStorage` so they survive page reloads
 *   and tab restores.
 * - The API key lives in `sessionStorage` only, so it is dropped when the
 *   tab closes. This is still XSS-readable; the configure page surfaces a
 *   banner explaining the tradeoff.
 *
 * The store never logs the API key. Callers are expected to mask via
 * `maskSecrets` (Story 307) before rendering errors.
 */

export const STORAGE_KEY_CONFIG = 'cwc-config';
export const SESSION_KEY_API_KEY = 'cwc-api-key';

export interface PersistableFormState {
  model?: string;
  maxTokens?: number;
  temperature?: number;
  baseUrl?: string;
  anthropicVersion?: string;
  apiKey?: string;
}

interface PersistedConfigShape {
  model: string;
  maxTokens: number;
  temperature?: number;
  baseUrl?: string;
  anthropicVersion?: string;
}

function safeGet(storage: Storage | undefined, key: string): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage | undefined, key: string, value: string): void {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // ignore quota / security exceptions — best-effort persistence.
  }
}

function safeRemove(storage: Storage | undefined, key: string): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore.
  }
}

/**
 * Read the persisted form state. Returns an empty object when nothing has
 * been saved yet, or when the stored payload is corrupt.
 */
export function loadPersistedConfig(
  local: Storage | undefined = globalThis.localStorage,
  session: Storage | undefined = globalThis.sessionStorage,
): PersistableFormState {
  const raw = safeGet(local, STORAGE_KEY_CONFIG);
  let parsed: Partial<PersistedConfigShape> = {};
  if (raw) {
    try {
      const candidate: unknown = JSON.parse(raw);
      if (candidate && typeof candidate === 'object') {
        parsed = candidate as Partial<PersistedConfigShape>;
      }
    } catch {
      // corrupt — drop silently.
    }
  }
  const apiKey = safeGet(session, SESSION_KEY_API_KEY) ?? undefined;
  const out: PersistableFormState = {};
  if (typeof parsed.model === 'string') out.model = parsed.model;
  if (typeof parsed.maxTokens === 'number') out.maxTokens = parsed.maxTokens;
  if (typeof parsed.temperature === 'number') out.temperature = parsed.temperature;
  if (typeof parsed.baseUrl === 'string') out.baseUrl = parsed.baseUrl;
  if (typeof parsed.anthropicVersion === 'string') {
    out.anthropicVersion = parsed.anthropicVersion;
  }
  if (apiKey) out.apiKey = apiKey;
  return out;
}

/**
 * Persist the non-secret fields to `localStorage`, and the API key to
 * `sessionStorage`. Empty / missing API keys clear the session-storage entry.
 */
export function savePersistedConfig(
  input: PersistableFormState,
  local: Storage | undefined = globalThis.localStorage,
  session: Storage | undefined = globalThis.sessionStorage,
): void {
  const persistable: PersistedConfigShape = {
    model: input.model ?? '',
    maxTokens: typeof input.maxTokens === 'number' ? input.maxTokens : 1024,
  };
  if (typeof input.temperature === 'number') {
    persistable.temperature = input.temperature;
  }
  if (typeof input.baseUrl === 'string' && input.baseUrl.length > 0) {
    persistable.baseUrl = input.baseUrl;
  }
  if (typeof input.anthropicVersion === 'string' && input.anthropicVersion.length > 0) {
    persistable.anthropicVersion = input.anthropicVersion;
  }
  safeSet(local, STORAGE_KEY_CONFIG, JSON.stringify(persistable));
  if (typeof input.apiKey === 'string' && input.apiKey.length > 0) {
    safeSet(session, SESSION_KEY_API_KEY, input.apiKey);
  } else {
    safeRemove(session, SESSION_KEY_API_KEY);
  }
}

/**
 * Drop both storage entries.
 */
export function clearPersistedConfig(
  local: Storage | undefined = globalThis.localStorage,
  session: Storage | undefined = globalThis.sessionStorage,
): void {
  safeRemove(local, STORAGE_KEY_CONFIG);
  safeRemove(session, SESSION_KEY_API_KEY);
}
